import { WorkOrderToolbarFilter } from '../components/WorkOrder/WorkOrderOverviewToolbar';

interface EntityOverviewZoomCenter {
  zoom?: number;
  center?: google.maps.LatLngLiteral;
}

interface LocalStorage {
  'EntityOverview.zoomCentersByTenant': Record<
    string,
    EntityOverviewZoomCenter
  >;
  'WorkOrderOverview.stateFilter': WorkOrderToolbarFilter;
  'WorkOrderOverview.filters': {
    selectedContractorIds: string[];
    selectedTagIds: string[];
    dueDateMin: string | null;
    dueDateMax: string | null;
    createdDateMin: string | null;
    createdDateMax: string | null;
    nameSearch: string;
    includeDrafts: boolean;
  };
  orgId: string;
}

function get(key: string): unknown {
  const item = localStorage.getItem(key);
  if (!item) {
    return null;
  }
  try {
    return JSON.parse(item);
  } catch (error) {
    if (typeof item === 'string') {
      return item;
    } else {
      return null;
    }
  }
}

function getWithFallback<T extends keyof LocalStorage>(
  key: T,
  fallback: LocalStorage[T],
): LocalStorage[T] {
  return (get(key) as LocalStorage[T]) || fallback;
}

function getWithoutFallback<T extends keyof LocalStorage>(
  key: T,
): LocalStorage[T] | null {
  return (get(key) as LocalStorage[T]) || null;
}

function set<T extends keyof LocalStorage>(
  key: T,
  value: LocalStorage[T],
): void {
  localStorage.setItem(key, JSON.stringify(value));
}

const isEntityOverviewZoomCenter = (
  value: unknown,
): value is EntityOverviewZoomCenter => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const zoomCenter = value as Record<string, unknown>;
  const center = zoomCenter.center;
  const hasValidCenter =
    center === undefined ||
    (typeof center === 'object' &&
      center !== null &&
      !Array.isArray(center) &&
      typeof (center as Record<string, unknown>).lat === 'number' &&
      typeof (center as Record<string, unknown>).lng === 'number');
  return (
    (zoomCenter.zoom === undefined || typeof zoomCenter.zoom === 'number') &&
    hasValidCenter
  );
};

const migrateLegacyEntityOverviewZoomCenter = (): void => {
  const legacyKey = 'EntityOverview.zoomCenter';
  if (localStorage.getItem(legacyKey) !== null) {
    localStorage.removeItem(legacyKey);
  }
};

const getEntityOverviewZoomCentersByTenant = (): Record<
  string,
  EntityOverviewZoomCenter
> => {
  const storedZoomCenters = get('EntityOverview.zoomCentersByTenant');
  if (
    typeof storedZoomCenters !== 'object' ||
    storedZoomCenters === null ||
    Array.isArray(storedZoomCenters)
  ) {
    return {};
  }
  return Object.entries(storedZoomCenters).reduce<
    Record<string, EntityOverviewZoomCenter>
  >((zoomCenters, [tenant, zoomCenter]) => {
    if (isEntityOverviewZoomCenter(zoomCenter)) {
      zoomCenters[tenant] = zoomCenter;
    }
    return zoomCenters;
  }, {});
};

export const getEntityOverviewZoomCenter = (
  tenant: string,
): EntityOverviewZoomCenter => {
  migrateLegacyEntityOverviewZoomCenter();
  const zoomCentersByTenant = getEntityOverviewZoomCentersByTenant();
  return zoomCentersByTenant[tenant] ?? {};
};

export const setEntityOverviewZoomCenter = (
  tenant: string,
  zoom: number,
  center: google.maps.LatLngLiteral,
): void => {
  migrateLegacyEntityOverviewZoomCenter();
  const zoomCentersByTenant = getEntityOverviewZoomCentersByTenant();
  set('EntityOverview.zoomCentersByTenant', {
    ...zoomCentersByTenant,
    [tenant]: { zoom, center },
  });
};

interface UseLocalStorage {
  get: typeof getWithFallback;
  getWithoutFallback: typeof getWithoutFallback;
  set: typeof set;
  getEntityOverviewZoomCenter: typeof getEntityOverviewZoomCenter;
  setEntityOverviewZoomCenter: typeof setEntityOverviewZoomCenter;
}

export const useLocalStorage = (): UseLocalStorage => {
  return {
    get: getWithFallback,
    set,
    getWithoutFallback,
    getEntityOverviewZoomCenter,
    setEntityOverviewZoomCenter,
  };
};
